import { useParams, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { COURSES } from '../home/components/TrainingCoursesBanner';
import { coursesExtended, courseModulesLocalized } from '@/mocks/coursesData';
import CourseDetailSEOHead from './CourseDetailSEOHead';
import CourseVideoPreview from './components/CourseVideoPreview';
import { useLang } from '@/hooks/useLang';

export default function CourseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [openModule, setOpenModule] = useState<number>(0);
  const [enrolled, setEnrolled] = useState(false);
  const [activeTab, setActiveTab] = useState<'outline' | 'objectives' | 'cases' | 'assignments'>('outline');
  const [activeModuleIdx, setActiveModuleIdx] = useState<number>(0);
  const moduleRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tabsSectionRef = useRef<HTMLDivElement>(null);

  const { t, lang } = useLang();
  const course = COURSES.find((c) => c.id === id);
  const extended = coursesExtended.find((e) => e.id === id);
  const isBcFundamentals = id === 'bc-fundamentals';

  // Get localized modules for current language
  const localizedModules = courseModulesLocalized[id ?? ''];
  const displayModules = localizedModules?.[lang] ?? localizedModules?.['zh'] ?? course?.modules ?? [];

  useEffect(() => {
    if (course) {
      moduleRefs.current = new Array(course.modules.length).fill(null);
    }
  }, [course?.id]);

  useEffect(() => {
    if (!course) return;
    const handleScroll = () => {
      if (activeTab !== 'outline') return;
      const viewportMid = window.scrollY + window.innerHeight * 0.4;
      let bestIdx = 0;
      for (let idx = 0; idx < moduleRefs.current.length; idx++) {
        const el = moduleRefs.current[idx];
        if (!el) continue;
        const absTop = el.getBoundingClientRect().top + window.scrollY;
        if (absTop <= viewportMid) bestIdx = idx;
      }
      setActiveModuleIdx(bestIdx);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    const timer = setTimeout(handleScroll, 50);
    return () => { window.removeEventListener('scroll', handleScroll); clearTimeout(timer); };
  }, [course, openModule, activeTab]);

  if (!course) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="text-gray-400 text-5xl mb-4"><i className="ri-emotion-sad-line"></i></div>
        <h2 className="text-xl font-bold text-gray-700 mb-2">{t('课程未找到', 'Course Not Found')}</h2>
        <button onClick={() => navigate('/courses')} className="mt-4 px-6 py-2 bg-[#1A0050] text-white rounded-full text-sm cursor-pointer whitespace-nowrap">{t('返回课程列表', 'Back to Courses')}</button>
      </div>
    );
  }

  const totalLessons = course.modules.reduce((sum, m) => sum + m.lessons.length, 0);

  const jumpToModule = (mi: number) => {
    setActiveTab('outline');
    setOpenModule(mi);
    setActiveModuleIdx(mi);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const el = moduleRefs.current[mi];
          if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top, behavior: 'smooth' });
          } else {
            tabsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 150);
      });
    });
  };

  // Localized UI strings
  const uiText = {
    backToCourses: t('返回课程列表', 'Back to Courses'),
    lessons: t('节课', 'Lessons'),
    modules: t('大模块', 'Modules'),
    students: t('名学员', 'Students'),
    highlights: t('课程核心亮点', 'Course Highlights'),
    outlineTab: t('课程大纲', 'Course Outline'),
    objectivesTab: t('学习目标', 'Learning Objectives'),
    assignmentsTab: t('作业与互动', 'Assignments'),
    outlineDesc: (mods: number, lessons: number, dur: string) =>
      `${mods} ${t('大模块', 'Modules')} · ${lessons} ${t('节课程', 'Lessons')} · ${dur}`,
    lessonCount: (n: number) => `${n}${t('节课', ' Lessons')}`,
    pending: t('待更新', 'Coming Soon'),
    objectivesTitle: t('完课后，你将能够：', 'After completing this course, you will be able to:'),
    coreKnowledge: t('核心知识体系', 'Core Knowledge Framework'),
    caseStudyTitle: t('真实案例深度剖析', 'Real Case Deep Analysis'),
    caseStudyDesc: (n: number) => `${n} ${t('个Case Study，结合真实项目，激活知识迁移', 'Case Studies embedded in the course, combining real projects to activate knowledge transfer')}`,
    background: t('背景', 'Background'),
    task: t('分析任务', 'Analysis Task'),
    discussion: t('课堂讨论题', 'Discussion Question'),
    assignmentsTitle: t('作业、互动与实操设计', 'Assignments, Interaction & Practice'),
    assignmentsSubtitle: t('课程不只是单向输出——有作业、有讨论、有实战，确保真正掌握', 'The course is not just one-way output — assignments, discussions, and practice ensure real mastery'),
    deliverable: t('提交物', 'Deliverable'),
    philosophyTitle: t('课程互动哲学', 'Course Interaction Philosophy'),
    philosophyContent: t(
      '清鉴元衡老师坚信：真正的学习发生在分析、争论和实际操作中，而非被动聆听。每门课程都精心设计了Case Study分析、小组讨论和个人作业，确保你不只是"懂了"，而是真正能独立应用。',
      'ChaInsight believes: real learning happens through analysis, debate, and hands-on practice — not passive listening. Every course is carefully designed with Case Study analysis, group discussions, and individual assignments to ensure you can truly apply what you learn independently.'
    ),
    catalogTitle: t('课程目录', 'Course Catalog'),
    chaptersLabel: (n: number) => `${n} ${t('章节', 'Chapters')}`,
    expanded: t('已展开', 'Expanded'),
    viewFullOutline: t('查看完整大纲', 'View Full Outline'),
    noticeTitle: t('课程开发公告', 'Course Development Notice'),
    noticeContent: t(
      '本课程视频内容正在专业录制中，预计将陆续上线。立即报名可锁定当前优惠价格，视频上线后自动开通观看权限。',
      'Course video content is being professionally recorded and will be released progressively. Enroll now to lock in the current price — viewing access will be automatically activated when videos go live.'
    ),
    relatedCourses: t('推荐配套课程', 'Recommended Courses'),
    enrollBtn: t('立即报名，抢先体验', 'Enroll Now — Early Access'),
    waitlistBtn: t('加入等待名单（免费）', 'Join Waitlist (Free)'),
    enrolledTitle: t('报名成功！', 'Enrollment Successful!'),
    enrolledDesc: t('视频课程上线后将通知您', 'You will be notified when video courses go live'),
    permanent: t('永久有效 · 含所有更新 · 支持开票', 'Lifetime Access · All Updates · Invoice Available'),
    hdVideo: t('高清视频课程', 'HD Video Courses'),
    materials: t('配套课件资料', 'Course Materials'),
    support: t('学员社群答疑', 'Community Q&A Support'),
    refund: t('7天无理由退款', '7-Day Money-Back Guarantee'),
    certificate: t('完课颁发学习证书', 'Completion Certificate'),
    whoShouldTake: t('适合谁学', 'Who Should Take This'),
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <CourseDetailSEOHead course={course} />

      {/* Back nav */}
      <div className="bg-white border-b border-gray-100 px-4 lg:px-8 py-3">
        <div className="max-w-[1440px] mx-auto">
          <button
            onClick={() => navigate('/courses')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-900 text-sm transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>{uiText.backToCourses}
          </button>
        </div>
      </div>

      {/* Book Cover Hero */}
      <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, #0A0A1A 0%, ${course.coverColor}44 50%, #0A0A1A 100%)` }}>
        <div className="absolute inset-0">
          {Array.from({ length: 40 }).map((_, i) => (
            <div key={i} className="absolute rounded-full bg-white" style={{ width: `${Math.random() * 2 + 0.5}px`, height: `${Math.random() * 2 + 0.5}px`, top: `${Math.random() * 100}%`, left: `${Math.random() * 100}%`, opacity: Math.random() * 0.4 + 0.05 }} />
          ))}
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-4 lg:px-8 py-12">
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            {/* Book Cover Card */}
            <div className="shrink-0 w-full lg:w-72">
              <div className="relative rounded-2xl overflow-hidden border border-white/20 group" style={{ boxShadow: `0 32px 80px ${course.coverColor}60, 0 0 0 1px rgba(255,255,255,0.1)` }}>
                {extended?.coverImage ? (
                  <img src={extended.coverImage} alt={`${course.title} ${t('课程封面', 'Course Cover')}`} className="w-full aspect-[5/7] object-cover object-top" />
                ) : (
                  <div className="w-full aspect-[5/7] flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${course.coverColor}, ${course.coverColor}88)` }}>
                    <i className={`${course.icon} text-white/30 text-8xl`}></i>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-5">
                  <div className="text-white/40 text-xs tracking-widest mb-1">COURSE {course.num}</div>
                  <div className="text-white text-xl tracking-[0.25em] mb-0.5" style={{ fontFamily: '"STKaiti", "KaiTi", "华文楷体", "楷体", serif', textShadow: '0 2px 12px rgba(0,0,0,0.8)', letterSpacing: '0.3em' }}>
                    {t('清鉴元衡', 'ChaInsight')}
                  </div>
                  <div className="text-white/50 text-xs tracking-widest">ChaInsight · {t('著', 'Author')}</div>
                </div>
                <div className="absolute top-3 left-3">
                  <span className="text-xs px-2.5 py-1 rounded-full font-semibold text-white" style={{ background: course.badgeColor }}>{course.badge}</span>
                </div>
                <div className="absolute top-3 right-3">
                  <span className="text-xs px-2 py-0.5 rounded-full text-white font-medium border border-white/30 bg-black/40">{course.level}</span>
                </div>
              </div>

              {extended && (
                <div className="mt-5 bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-sm">
                  <div className="flex items-start gap-2 mb-3">
                    <i className="ri-double-quotes-l text-amber-400 text-2xl shrink-0 mt-0.5"></i>
                    <p className="text-white/80 text-xs leading-relaxed italic">{extended.expertQuote}</p>
                  </div>
                  <div className="border-t border-white/10 pt-3">
                    <div className="text-amber-400 font-bold text-xs">{extended.expertName}</div>
                    <div className="text-white/40 text-xs mt-0.5">{extended.expertTitle}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Course Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs px-3 py-1 rounded-full font-semibold text-white bg-white/15">COURSE {course.num}</span>
                <span className="text-xs px-3 py-1 rounded-full font-semibold text-white" style={{ background: course.badgeColor }}>{course.badge}</span>
              </div>
              <h1 className="text-3xl lg:text-4xl font-black text-white mb-3 leading-tight">{course.title}</h1>
              <p className="text-white/70 text-base mb-4 italic">{course.subtitle}</p>
              {extended && <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-2xl">{extended.courseIntro}</p>}
              <div className="flex flex-wrap gap-5 text-white/60 text-sm mb-8">
                <span className="flex items-center gap-2"><i className="ri-time-line text-amber-400"></i>{course.duration}</span>
                <span className="flex items-center gap-2"><i className="ri-play-list-line text-amber-400"></i>{totalLessons}{uiText.lessons}</span>
                <span className="flex items-center gap-2"><i className="ri-user-3-line text-amber-400"></i>{course.students}{uiText.students}</span>
                <span className="flex items-center gap-2"><i className="ri-book-2-line text-amber-400"></i>{course.modules.length}{uiText.modules}</span>
              </div>

              {extended && extended.whoShouldTake.length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6 max-w-2xl">
                  <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                    <i className="ri-user-heart-line text-amber-400"></i>{uiText.whoShouldTake}
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {extended.whoShouldTake.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 text-white/60 text-xs">
                        <i className="ri-check-line text-amber-400 shrink-0 mt-0.5"></i>{w}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-white rounded-2xl p-6 max-w-sm">
                <div className="text-3xl font-black text-[#1A0050] mb-1">{course.price}</div>
                <div className="text-gray-400 text-xs mb-4">{uiText.permanent}</div>
                {enrolled ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center mb-4">
                    <i className="ri-checkbox-circle-line text-green-500 text-2xl mb-2 block"></i>
                    <div className="text-green-800 font-bold text-sm">{uiText.enrolledTitle}</div>
                    <div className="text-green-600 text-xs mt-1">{uiText.enrolledDesc}</div>
                  </div>
                ) : (
                  <button onClick={() => setEnrolled(true)} className="w-full py-3 bg-[#1A0050] text-white rounded-xl font-bold text-base mb-3 hover:bg-[#2E1065] transition-all cursor-pointer whitespace-nowrap">
                    {uiText.enrollBtn}
                  </button>
                )}
                <button className="w-full py-2.5 border border-[#1A0050]/30 text-[#1A0050] rounded-xl font-semibold text-sm hover:bg-[#1A0050]/5 transition-all cursor-pointer whitespace-nowrap mb-4">
                  {uiText.waitlistBtn}
                </button>
                <div className="space-y-2 text-xs text-gray-500">
                  <div className="flex items-center gap-2"><i className="ri-video-line text-[#1A0050]"></i>{uiText.hdVideo}</div>
                  <div className="flex items-center gap-2"><i className="ri-file-pdf-line text-[#1A0050]"></i>{uiText.materials}</div>
                  <div className="flex items-center gap-2"><i className="ri-customer-service-2-line text-[#1A0050]"></i>{uiText.support}</div>
                  <div className="flex items-center gap-2"><i className="ri-shield-check-line text-[#1A0050]"></i>{uiText.refund}</div>
                  <div className="flex items-center gap-2"><i className="ri-award-line text-[#1A0050]"></i>{uiText.certificate}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1 min-w-0 space-y-8">
            {isBcFundamentals && <div className="mb-2"><CourseVideoPreview /></div>}

            {/* Highlights */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100">
              <h2 className="font-black text-gray-900 text-lg mb-4 flex items-center gap-2">
                <i className="ri-star-line text-amber-400"></i>{uiText.highlights}
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {course.highlights.map((h, i) => (
                  <div key={i} className="flex items-start gap-3 bg-[#1A0050]/5 rounded-xl p-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-[#1A0050] rounded-full shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-[10px]"></i>
                    </div>
                    <span className="text-gray-700 text-sm leading-relaxed">{h}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div ref={tabsSectionRef} className="bg-white rounded-2xl border border-gray-100 overflow-hidden scroll-mt-4">
              <div className="flex border-b border-gray-100 overflow-x-auto">
                {[
                  { key: 'outline', label: uiText.outlineTab, icon: 'ri-list-ordered-2' },
                  { key: 'objectives', label: uiText.objectivesTab, icon: 'ri-flag-2-line' },
                  { key: 'cases', label: 'Case Study', icon: 'ri-folder-open-line' },
                  { key: 'assignments', label: uiText.assignmentsTab, icon: 'ri-pencil-ruler-2-line' },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as typeof activeTab)}
                    className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold whitespace-nowrap transition-all cursor-pointer border-b-2 ${activeTab === tab.key ? 'border-[#1A0050] text-[#1A0050] bg-[#1A0050]/3' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                  >
                    <i className={tab.icon}></i>{tab.label}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {/* Course Outline */}
                {activeTab === 'outline' && (
                  <div>
                    <p className="text-gray-400 text-sm mb-5">{uiText.outlineDesc(displayModules.length, totalLessons, course.duration)}</p>
                    <div className="space-y-3">
                      {displayModules.map((mod, mi) => (
                        <div key={mi} ref={(el) => { moduleRefs.current[mi] = el; }} id={`module-item-${mi}`} className="border border-gray-100 rounded-xl overflow-hidden scroll-mt-24">
                          <button
                            className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors cursor-pointer text-left"
                            onClick={() => setOpenModule(openModule === mi ? -1 : mi)}
                          >
                            <div className="w-8 h-8 flex items-center justify-center rounded-xl font-black text-sm text-white shrink-0" style={{ background: course.coverColor }}>
                              {mi + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-gray-900 text-sm">{mod.title}</div>
                              <div className="text-xs text-gray-400 mt-0.5">{uiText.lessonCount(mod.lessons.length)}</div>
                            </div>
                            <i className={`text-gray-400 transition-transform duration-200 ${openModule === mi ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                          </button>
                          {openModule === mi && (
                            <div className="border-t border-gray-50 divide-y divide-gray-50">
                              {mod.lessons.map((lesson, li) => (
                                <div key={li} className="flex items-center gap-3 px-4 py-3 bg-gray-50/50">
                                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-200 shrink-0">
                                    <i className="ri-lock-line text-gray-400 text-xs"></i>
                                  </div>
                                  <span className="text-sm text-gray-600">{lesson}</span>
                                  <span className="ml-auto text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full shrink-0">{uiText.pending}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Learning Objectives */}
                {activeTab === 'objectives' && extended && (
                  <div>
                    <h3 className="font-black text-gray-900 text-base mb-5 flex items-center gap-2">
                      <i className="ri-flag-2-line text-[#1A0050]"></i>{uiText.objectivesTitle}
                    </h3>
                    <div className="space-y-3 mb-8">
                      {extended.learningObjectives.map((obj, i) => (
                        <div key={i} className="flex items-start gap-3 p-4 bg-[#1A0050]/4 rounded-xl">
                          <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#1A0050] text-white text-xs font-black shrink-0">{i + 1}</div>
                          <p className="text-gray-700 text-sm leading-relaxed">{obj}</p>
                        </div>
                      ))}
                    </div>
                    <h3 className="font-black text-gray-900 text-base mb-4 flex items-center gap-2">
                      <i className="ri-book-3-line text-amber-500"></i>{uiText.coreKnowledge}
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {extended.keyPoints.map((kp, i) => (
                        <div key={i} className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-100">
                          <i className="ri-arrow-right-s-line text-amber-500 shrink-0 mt-0.5"></i>
                          <span className="text-gray-700 text-sm leading-relaxed">{kp}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Case Studies */}
                {activeTab === 'cases' && extended && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-[#1A0050]/10 rounded-xl">
                        <i className="ri-folder-open-line text-[#1A0050]"></i>
                      </div>
                      <div>
                        <h3 className="font-black text-gray-900 text-base">{uiText.caseStudyTitle}</h3>
                        <p className="text-gray-400 text-xs">{uiText.caseStudyDesc(extended.caseStudies.length)}</p>
                      </div>
                    </div>
                    {extended.caseStudies.map((cs, i) => (
                      <div key={i} className="border border-gray-100 rounded-2xl overflow-hidden">
                        <div className="p-5 bg-[#1A0050]/3">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xs bg-[#1A0050] text-white px-2.5 py-1 rounded-full font-bold">Case {i + 1}</span>
                            <h4 className="font-bold text-gray-900 text-sm">{cs.title}</h4>
                          </div>
                        </div>
                        <div className="p-5 space-y-4">
                          <div>
                            <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                              <i className="ri-information-line text-[#1A0050]"></i>{uiText.background}
                            </div>
                            <p className="text-gray-700 text-sm leading-relaxed">{cs.background}</p>
                          </div>
                          <div>
                            <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                              <i className="ri-task-line text-amber-500"></i>{uiText.task}
                            </div>
                            <p className="text-gray-700 text-sm leading-relaxed bg-amber-50 rounded-xl p-3 border border-amber-100">{cs.task}</p>
                          </div>
                          <div>
                            <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                              <i className="ri-discuss-line text-green-600"></i>{uiText.discussion}
                            </div>
                            <p className="text-gray-700 text-sm leading-relaxed bg-green-50 rounded-xl p-3 border border-green-100">{cs.discussion}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Assignments */}
                {activeTab === 'assignments' && extended && (
                  <div className="space-y-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-[#1A0050]/10 rounded-xl">
                        <i className="ri-pencil-ruler-2-line text-[#1A0050]"></i>
                      </div>
                      <div>
                        <h3 className="font-black text-gray-900 text-base">{uiText.assignmentsTitle}</h3>
                        <p className="text-gray-400 text-xs">{uiText.assignmentsSubtitle}</p>
                      </div>
                    </div>
                    {extended.assignments.map((asgn, i) => (
                      <div key={i} className="border border-gray-100 rounded-2xl p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-black text-white px-2.5 py-1 rounded-full" style={{ background: asgn.type === '个人作业' ? '#1A0050' : asgn.type === '小组讨论' ? '#059669' : asgn.type === '实战演练' ? '#D97706' : asgn.type === 'Case Study' ? '#2563EB' : '#7C3AED' }}>{asgn.type}</span>
                            <h4 className="font-bold text-gray-900 text-sm">{asgn.title}</h4>
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed mb-3">{asgn.description}</p>
                        <div className="flex items-start gap-2 bg-gray-50 rounded-xl p-3 border border-gray-100">
                          <i className="ri-file-list-3-line text-[#1A0050] shrink-0 mt-0.5"></i>
                          <div>
                            <div className="text-xs font-bold text-gray-500 mb-0.5">{uiText.deliverable}</div>
                            <p className="text-gray-700 text-xs leading-relaxed">{asgn.deliverable}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mt-4">
                      <div className="flex items-start gap-3">
                        <i className="ri-lightbulb-line text-amber-500 text-xl shrink-0"></i>
                        <div>
                          <div className="font-bold text-amber-800 text-sm mb-1">{uiText.philosophyTitle}</div>
                          <p className="text-amber-700 text-xs leading-relaxed">{uiText.philosophyContent}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-72 shrink-0">
            <div className="sticky top-4 space-y-4">
              {/* Course Catalog Navigation */}
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-map-pin-line text-[#1A0050]"></i>
                    </div>
                    {uiText.catalogTitle}
                  </h3>
                  <span className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">{uiText.chaptersLabel(displayModules.length)}</span>
                </div>

                <div className="px-4 py-2.5 bg-[#1A0050]/3 border-b border-gray-100 flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-300" style={{ width: `${((activeModuleIdx + 1) / displayModules.length) * 100}%`, background: course.coverColor }} />
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{activeModuleIdx + 1}/{displayModules.length}</span>
                </div>

                <div className="py-2 max-h-[420px] overflow-y-auto">
                  {displayModules.map((mod, mi) => {
                    const isActive = activeModuleIdx === mi;
                    const isOpen = openModule === mi;
                    return (
                      <button key={mi} onClick={() => jumpToModule(mi)} className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-all cursor-pointer group ${isActive ? 'bg-[#1A0050]/5' : 'hover:bg-gray-50'}`}>
                        <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-black shrink-0 mt-0.5 transition-all ${isActive ? 'text-white' : 'text-white/80'}`} style={{ background: isActive ? course.coverColor : `${course.coverColor}70` }}>
                          {mi + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-semibold leading-snug transition-colors line-clamp-2 ${isActive ? 'text-[#1A0050]' : 'text-gray-600 group-hover:text-gray-900'}`}>{mod.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] text-gray-400">{mod.lessons.length}{t('节', ' L')}</span>
                            {isOpen && <span className="text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full font-medium">{uiText.expanded}</span>}
                          </div>
                        </div>
                        {isActive && <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5"><i className="ri-arrow-right-s-line text-[#1A0050]"></i></div>}
                      </button>
                    );
                  })}
                </div>

                <div className="px-4 py-3 border-t border-gray-100">
                  <button onClick={() => { setActiveTab('outline'); tabsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className="w-full py-2 text-xs text-[#1A0050] font-semibold bg-[#1A0050]/5 rounded-xl hover:bg-[#1A0050]/10 transition-colors cursor-pointer flex items-center justify-center gap-1.5 whitespace-nowrap">
                    <i className="ri-eye-line"></i>{uiText.viewFullOutline}
                  </button>
                </div>
              </div>

              {/* Notice */}
              <div className="bg-amber-50 border border-amber-200/60 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-notification-3-line text-amber-600 text-sm"></i>
                  <span className="text-amber-800 font-bold text-sm">{uiText.noticeTitle}</span>
                </div>
                <p className="text-amber-700 text-xs leading-relaxed">{uiText.noticeContent}</p>
              </div>

              {/* Related Courses */}
              <div className="bg-white rounded-2xl p-5 border border-gray-100">
                <h3 className="font-bold text-gray-900 text-sm mb-3">{uiText.relatedCourses}</h3>
                {COURSES.filter((c) => c.id !== course.id).slice(0, 4).map((c) => (
                  <button key={c.id} onClick={() => navigate(`/courses/${c.id}`)} className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded-xl transition-colors cursor-pointer text-left mb-1">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0" style={{ background: c.coverColor }}>
                      <i className={`${c.icon} text-white text-sm`}></i>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-gray-800 truncate">{c.title}</div>
                      <div className="text-xs text-[#1A0050]">{c.price}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
